import { Database, Json } from "supabase/database.types";

import { createClient } from "./supabase";

export type BadgeDefinition =
  Database["public"]["Tables"]["badge_definitions"]["Row"];

export interface GamesStats {
  bestGameSlug: string;
  highestScore: number;
  totalGamesAttempted: number;
  userRank: number;
}

export interface GamificationEvent {
  created_at: string;
  event_type:
    | "flashcard_deck_created"
    | "quiz_completed"
    | "streak_milestone"
    | "worksheet_created";
  id: string;
  metadata: Record<string, unknown>;
  points_earned: number;
  user_id: string;
}

export interface GamificationStats {
  averageAccuracy: number;
  currentStreak: number;
  longestStreak: number;
  maxPoints: number;
  minPoints: number;
  rank: null | number;
  totalBadges: number;
  totalPoints: number;
  totalPracticeTests: number;
  weeklyPoints: number;
}

export interface LeaderboardScore {
  id: string;
  score: number;
  subject_slug: string;
  updated_at: string;
  user_id: string;
  weekly_points: number;
}

export interface Notification {
  created_at: string;
  data: Record<string, unknown>;
  id: string;
  message: string;
  read: boolean;
  title: string;
  type:
    | "badge_earned"
    | "challenge_completed"
    | "challenge_received"
    | "leaderboard_rank"
    | "streak_milestone";
  user_id: string;
}

export interface PracticeTestAttempt {
  attempted_date: string;
  created_at: string;
  id: string;
  practice_test_id?: string;
  questions_answered: number;
  score: number;
  subject_name: string;
  subject_slug: string;
  time_seconds: number;
  user_id: string;
}

export interface UserBadge {
  badge?: BadgeDefinition;
  badge_slug: string;
  earned_at: string;
  id: string;
  user_id: string;
}

export interface UserStreak {
  current_streak: number;
  last_activity_date: string;
  longest_streak: number;
  updated_at: string;
  user_id: string;
}

class GamificationService {
  private supabase = createClient();

  // Create a notification
  async createNotification(
    userId: string,
    type: Notification["type"],
    title: string,
    message: string,
    data: Json = {}
  ) {
    try {
      await this.supabase
        .from("notifications")
        .insert({
          data,
          message,
          title,
          type,
          user_id: userId,
        })
        .throwOnError();
    } catch (error) {
      console.error("Error creating notification:", error);
      throw error;
    }
  }

  // Get all available badge definitions
  async getBadgeDefinitions() {
    try {
      const { data, error } = await this.supabase
        .from("badge_definitions")
        .select("*");

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching badge definitions:", error);
      throw error;
    }
  }

  async getCurrentScore() {
    return await this.supabase
      .from("profile_rank")
      .select("score, level, level_title, level_min_score, level_max_score")
      .single()
      .throwOnError();
  }

  async getGamesStats(userId: string): Promise<GamesStats> {
    try {
      const { data, error } = await this.supabase
        .rpc("get_games_stats", { user_id_param: userId })
        .single();

      if (error) throw error;

      return {
        bestGameSlug: data?.best_game_slug || "",
        highestScore: data?.highest_score || 0,
        totalGamesAttempted: data?.total_games_attempted || 0,
        userRank: data?.user_rank || 0,
      };
    } catch (error) {
      console.error("Error fetching games stats:", error);
      throw error;
    }
  }

  // Get recent practice test attempts
  async getRecentPracticeTestAttempts(userId: string, limit = 10) {
    try {
      const { data, error } = await this.supabase
        .from("practice_test_attempts")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching recent practice test attempts:", error);
      throw error;
    }
  }

  // Get user's badges
  async getUserBadges(userId: string) {
    const { data } = await this.supabase
      .from("badges")
      .select("earned_at, badge:badge_definitions(*)")
      .eq("user_id", userId)
      .order("earned_at", { ascending: false })
      .throwOnError();

    return (data || []).map(({ badge, earned_at }) => ({
      ...badge,
      earned_at,
    }));
  }

  // Get user's gamification events
  async getUserGamificationEvents(userId: string, limit = 20) {
    try {
      const { data, error } = await this.supabase
        .from("gamification_events")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching gamification events:", error);
      throw error;
    }
  }

  // Get user's notifications
  async getUserNotifications(userId: string, limit = 10) {
    try {
      const { data, error } = await this.supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  }

  // Get user's rank
  async getUserRank(
    userId: string,
    subjectSlug?: string
  ): Promise<null | number> {
    try {
      let query = this.supabase
        .from("leaderboard_scores")
        .select("user_id, score")
        .order("score", { ascending: false });

      if (subjectSlug) {
        query = query.eq("subject_slug", subjectSlug);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (!data) return null;

      // Find user's score
      const userScore = data.find((score) => score.user_id === userId)?.score;
      if (!userScore) return null;

      // Find rank (position in sorted list)
      const rank = data.findIndex((score) => score.score === userScore) + 1;
      return rank;
    } catch (error) {
      console.error("Error fetching user rank:", error);
      return null;
    }
  }

  // Get user's gamification statistics
  async getUserStats(userId: string): Promise<GamificationStats> {
    try {
      // Get profile rank data
      const { data: profileRank, error: profileError } = await this.supabase
        .from("profile_rank")
        .select("score, level, level_title, level_min_score, level_max_score")
        .eq("user_id", userId)
        .maybeSingle();

      if (profileError && profileError.code !== "PGRST116") throw profileError;

      // Get streak data
      const { data: streak, error: streakError } = await this.supabase
        .from("streaks")
        .select("current_streak, longest_streak")
        .eq("user_id", userId)
        .maybeSingle();

      if (streakError && streakError.code !== "PGRST116") throw streakError;

      // Get badges count
      const { count: badgeCount, error: badgeError } = await this.supabase
        .from("badges")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId);

      if (badgeError) throw badgeError;

      // Get practice test attempts for accuracy calculation
      const { data: attempts, error: attemptsError } = await this.supabase
        .from("practice_test_attempts")
        .select("score, questions_answered")
        .eq("user_id", userId);

      if (attemptsError) throw attemptsError;

      // Get global rank
      const { data: allScores, error: rankError } = await this.supabase
        .from("profile_rank")
        .select("user_id, score")
        .order("score", { ascending: false });

      if (rankError) throw rankError;

      // Calculate statistics
      const totalPoints = profileRank?.score || 0;
      const maxPoints = profileRank?.level_max_score || 0;
      const minPoints = profileRank?.level_min_score || 0;
      const currentStreak = streak?.current_streak || 0;
      const longestStreak = streak?.longest_streak || 0;
      const totalBadges = badgeCount || 0;
      const totalPracticeTests = attempts?.length || 0;

      // Calculate average accuracy
      const totalScore =
        attempts?.reduce((sum, attempt) => sum + attempt.score, 0) || 0;
      const averageAccuracy =
        attempts && attempts.length > 0
          ? Math.round(totalScore / attempts.length)
          : 0;

      // Calculate global rank
      let globalRank = null;
      if (allScores && profileRank) {
        const userIndex = allScores.findIndex(
          (score) => score.user_id === userId
        );
        globalRank = userIndex >= 0 ? userIndex + 1 : null;
      }

      // Calculate weekly points (this would need to be implemented based on your business logic)
      const weeklyPoints = 0; // TODO: Implement weekly points calculation

      return {
        averageAccuracy,
        currentStreak,
        longestStreak,
        maxPoints,
        minPoints,
        rank: globalRank,
        totalBadges,
        totalPoints,
        totalPracticeTests,
        weeklyPoints,
      };
    } catch (error) {
      console.error("Error fetching user stats:", error);
      throw error;
    }
  }

  // Get user's streak information
  async getUserStreak(userId: string) {
    try {
      const { data, error } = await this.supabase
        .from("streaks")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      return data;
    } catch (error) {
      console.error("Error fetching user streak:", error);
      return null;
    }
  }

  // Check if user has earned a specific badge
  async hasBadge(userId: string, badgeSlug: string): Promise<boolean> {
    try {
      const { data, error } = await this.supabase
        .from("badges")
        .select("id")
        .eq("user_id", userId)
        .eq("badge_slug", badgeSlug)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return !!data;
    } catch (error) {
      console.error("Error checking badge:", error);
      return false;
    }
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId: string) {
    try {
      const { error } = await this.supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) throw error;
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  }

  // Record flashcard deck creation and award points
  async recordFlashcardDeckCreation(
    userId: string,
    deckId: string,
    subjectSlug: string
  ): Promise<void> {
    try {
      // Call the database function that handles all gamification logic
      const { error } = await this.supabase.rpc(
        "process_flashcard_deck_creation",
        {
          p_deck_id: deckId,
          p_subject_slug: subjectSlug,
          p_user_id: userId,
        }
      );

      if (error) throw error;
    } catch (error) {
      console.error("Error recording flashcard deck creation:", error);
      throw error;
    }
  }

  async recordGameAttempt(
    gameSlug: "crossmath" | "flashcards" | "number-cards" | "speed-challenge",
    score: number = 0,
    timeSeconds: number = 0,
    difficulty: number = 1,
    metadata: Json | null = null
  ) {
    await this.supabase.from("game_attempts").insert({
      difficulty,
      game_slug: gameSlug,
      metadata,
      score,
      time_seconds: Math.floor(timeSeconds),
    });
  }

  // Record a quiz completion and trigger gamification checks
  async recordQuizCompletion(
    userId: string,
    quizId: null | string,
    subjectSlug: string,
    score: number,
    totalQuestions: number,
    timeSeconds: number,
    pointsEarned: number
  ): Promise<void> {
    try {
      // Call the database function that handles all gamification logic
      const { error } = await this.supabase.rpc("process_quiz_completion", {
        p_points_earned: pointsEarned,
        p_quiz_id: quizId ?? "",
        p_score: score,
        p_subject_slug: subjectSlug,
        p_time_seconds: timeSeconds,
        p_total_questions: totalQuestions,
        p_user_id: userId,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error recording quiz completion:", error);
      throw error;
    }
  }

  // Record worksheet creation and award points
  async recordWorksheetCreation(
    userId: string,
    worksheetId: string,
    subjectSlug: string
  ): Promise<void> {
    try {
      // Call the database function that handles all gamification logic
      const { error } = await this.supabase.rpc("process_worksheet_creation", {
        p_subject_slug: subjectSlug,
        p_user_id: userId,
        p_worksheet_id: worksheetId,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error recording worksheet creation:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const gamificationService = new GamificationService();
